import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  Category as CategoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { CategoryService } from '../services/categoryService';
import { Category } from '../lib/supabase';

const CategoriesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is the job categories page
  const isJobCategoriesPage = location.pathname === '/job-categories';
  
  // State for categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedCategories = await CategoryService.getCategories();
        
        // Filter categories based on page type
        let filteredCategories;
        if (isJobCategoriesPage) {
          // On job categories page, show only job categories
          filteredCategories = fetchedCategories.filter(cat => cat.is_active && cat.is_job_category);
        } else {
          // On regular categories page, show only product categories
          filteredCategories = fetchedCategories.filter(cat => cat.is_active && !cat.is_job_category);
        }
        
        setCategories(filteredCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError('Failed to load categories');
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isJobCategoriesPage]);

  const handleCategoryClick = (category: Category) => {
    // Properly encode the category name for URL to handle special characters
    const encodedCategoryName = encodeURIComponent(category.name.toLowerCase());
    
    // Navigate to the appropriate page based on category type
    if (category.is_job_category) {
      navigate(`/all-jobs?category=${encodedCategoryName}`);
    } else {
      navigate(`/all-products?category=${encodedCategoryName}`);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{ mb: 3, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.7)' } }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackToHome}
              sx={{
                color: 'rgba(255,255,255,0.8)',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { color: 'white' },
              }}
            >
              <HomeIcon fontSize="small" />
              Home
            </Link>
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              {isJobCategoriesPage ? 'Job Categories' : 'Categories'}
            </Typography>
          </Breadcrumbs>

          {/* Page Title */}
          <Box sx={{ textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                fontWeight="bold"
                gutterBottom
              >
                {isJobCategoriesPage ? 'Job Categories' : 'All Categories'}
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{ opacity: 0.9, mb: 3 }}
              >
                {isJobCategoriesPage 
                  ? 'Explore career opportunities across all job categories' 
                  : 'Explore our complete collection of product categories'
                }
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToHome}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Back to Home
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Categories Grid Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Loading categories...
              </Typography>
            </Box>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="error" gutterBottom>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Box>
        ) : categories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CategoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No categories available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Check back later for new categories.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Categories Stats */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {categories.length} {isJobCategoriesPage ? 'Job Categories' : 'Categories'} Available
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isJobCategoriesPage 
                  ? 'Discover career opportunities across all job categories' 
                  : 'Discover products across all categories'
                }
              </Typography>
            </Box>

            {/* Categories Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              {categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Card
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    {/* Category Image */}
                    <CardMedia
                      component="img"
                      height="200"
                      image={category.image_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop'}
                      alt={category.name}
                      sx={{ objectFit: 'cover' }}
                    />

                    {/* Category Content */}
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {category.name}
                      </Typography>
                      
                      {category.description && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ mb: 2, minHeight: '2.5em' }}
                        >
                          {category.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {category.product_count > 0 ? `${category.product_count} products` : 'Coming soon'}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary">
                          Updated: {new Date(category.updated_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>

                    {/* Hover Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8))',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" color="white">
                        Explore Products
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              ))}
            </Box>

            {/* Back to Home Button */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToHome}
                sx={{ px: 4, py: 1.5 }}
              >
                Back to Home
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default CategoriesPage;
